import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BookingForm = () => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    date: '',
    time: '',
    address: '',
    postcode: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.service || !formData.date || !formData.address || !formData.postcode) {
      toast.error(language === 'en' ? 'Please fill in all required fields' : 'Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία');
      return;
    }

    try {
      const BACKEND_URL = "https://nea-clean-api-18290f.netlify.app/.netlify/functions";

      const response = await fetch(`${BACKEND_URL}/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(t.booking.form.success);
        
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          date: '',
          time: '',
          address: '',
          postcode: '',
          message: ''
        });
      } else {
        toast.error(data.message || t.booking.form.error);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(t.booking.form.error);
    }
  };

  const serviceOptions = t.booking.serviceOptions || [];

  return (
    <section id="booking" className="pad-2xl" style={{ background: '#e8f4f8' }}>
      <div className="container max-w-3xl">
        <h2 className="text-4xl font-bold text-center mb-2" style={{ color: '#1e3a5f', fontStyle: 'italic' }}>
          {t.booking.title}
        </h2>
        <p className="text-center mb-8" style={{ color: '#5a7a8a' }}>
          {t.booking.subtitle}
        </p>
        
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <CardContent className="p-8 bg-white">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name and Phone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                    {t.booking.form.name} *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    {t.booking.form.phone} *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {t.booking.form.email} *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                />
              </div>

              {/* Service Type */}
              <div className="space-y-2">
                <Label htmlFor="service" className="text-sm font-medium text-gray-700">
                  {t.booking.form.service} *
                </Label>
                <Select onValueChange={handleSelectChange} value={formData.service}>
                  <SelectTrigger className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400">
                    <SelectValue placeholder={t.booking.form.servicePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceOptions.map((option, idx) => (
                      <SelectItem key={idx} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="date" className="text-sm font-medium text-gray-700">
                    {t.booking.form.date} *
                  </Label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-sm font-medium text-gray-700">
                    {t.booking.form.time}
                  </Label>
                  <Input
                    id="time"
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                  />
                </div>
              </div>

              {/* Property Address */}
              <div className="space-y-2">
                <Label htmlFor="address" className="text-sm font-medium text-gray-700">
                  {t.booking.form.address} *
                </Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                />
              </div>

              {/* Post Code */}
              <div className="space-y-2">
                <Label htmlFor="postcode" className="text-sm font-medium text-gray-700">
                  {t.booking.form.postcode || (language === 'en' ? 'Post Code' : 'Ταχυδρομικός Κώδικας')} *
                </Label>
                <Input
                  id="postcode"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleInputChange}
                  required
                  placeholder={language === 'en' ? 'e.g. 10557' : 'π.χ. 10557'}
                  className="h-12 border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                />
              </div>

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                  {t.booking.form.message}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3}
                  className="resize-y min-h-[100px] border-gray-200 rounded-lg focus:border-sky-400 focus:ring-sky-400"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 text-lg font-semibold rounded-full"
                style={{ 
                  background: 'linear-gradient(135deg, #87CEEB 0%, #5DADE2 100%)',
                  color: 'white'
                }}
              >
                {t.booking.form.submit}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default BookingForm;
